import { useState, useRef, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';
import { MarginProps } from 'styled-system';
import { rgba } from 'polished';

import {
	Button,
	Heading,
	Separator,
	IconButton,
	PlainInput,
	Avatar,
	Skeleton,
} from '@treams/treams-ui';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import * as graphql from 'generated/graphql';
import { CloseIcon, SendIcon } from '@treams/treams-icons';

const slideIn = keyframes`
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: none; }
`;

const bulletBefore = css`
	&::before {
		display: block;
		height: 100%;
		content: '●';
		margin-right: 0.5em;
	}
`;

const ChatBubbleContainer = styled(Box)<{ isMagic?: boolean; isBulletPoint?: boolean }>`
	display: flex;
	flex-direction: row;
	background: ${(props) =>
		rgba(props.isMagic ? props.theme.colors.primary : props.theme.colors.blue, 0.1)};
	border-radius: 8px;
	color: ${(props) => (props.isMagic ? props.theme.colors.primary : props.theme.colors.blue)};
	line-height: 20px;
	animation: 1s cubic-bezier(0, 1, 0, 1) ${slideIn};
	animation-fill-mode: backwards;
	padding: 10px 12px;

	${(props) => props.isBulletPoint && bulletBefore};
`;

function ChatBubble(props: { isMagic?: boolean; message: string }) {
	const isBulletPoint = props.message.startsWith('• ');

	return (
		<ChatBubbleContainer isMagic={props.isMagic} isBulletPoint={isBulletPoint}>
			{isBulletPoint ? props.message.slice(2) : props.message}
		</ChatBubbleContainer>
	);
}

const ChatAvatarBox = styled(Box)`
	animation: 1s cubic-bezier(0, 1, 0, 1) ${slideIn};
	animation-fill-mode: backwards;
`;

const TypingIndicatorBubble = styled(Flex)`
	background: ${(props) => props.theme.colors.white};
	border-radius: 8px;
	line-height: 20px;
	animation: 1s cubic-bezier(0, 1, 0, 1) ${slideIn};
	animation-fill-mode: backwards;
	animation-delay: 300ms;
	float: left;
`;

function TypingIndicator(props: MarginProps) {
	return (
		<TypingIndicatorBubble p={3} {...props}>
			<Skeleton.Box width={6} height={6} style={{ borderRadius: 4 }} mr={1} />
			<Skeleton.Box width={6} height={6} style={{ borderRadius: 4 }} mr={1} />
			<Skeleton.Box width={6} height={6} style={{ borderRadius: 4 }} />
		</TypingIndicatorBubble>
	);
}

const Container = styled(Box)`
	display: flex;
	flex-direction: column;
	height: 500px;
	justify-content: space-between;
	background: ${(props) => props.theme.colors.lightBlueLight};
	overflow-y: auto;
`;

function BubbleGroup(props: {
	isMagic?: boolean;
	messages: string[];
	onMessageDisplayed?: () => void;
	onStoppedTyping?: () => void;
}) {
	const { t } = useTranslation();
	const [messagesShown, setMessagesShown] = useState(0);

	if (!props.isMagic && messagesShown != props.messages.length) {
		setMessagesShown(props.messages.length);
	}

	const typingOnly = props.messages.length === 0;
	const isTyping = props.isMagic && (typingOnly || messagesShown < props.messages.length);

	const viewer = useAuthenticatedUser();
	const magicIdentity = {
		firstName: t('magic.identity.firstName'),
		lastName: t('magic.identity.lastName'),
	} as graphql.User;

	const bubbles = props.messages.slice(0, messagesShown).map((message, i) => (
		<Flex mt={i === 0 ? 0 : 1} key={i}>
			<ChatBubble isMagic={props.isMagic} message={message} />
		</Flex>
	));

	const showNewMessage = (): boolean => {
		if (messagesShown === props.messages.length) {
			return false;
		}

		if (messagesShown + 1 == props.messages.length) {
			props.onStoppedTyping?.();
		}

		setMessagesShown(messagesShown + 1);

		return true;
	};

	useEffect(() => {
		if (!typingOnly && messagesShown < props.messages.length) {
			const timer = setTimeout(() => showNewMessage(), 1000);
			return () => clearTimeout(timer);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messagesShown, typingOnly]);

	useEffect(() => {
		props.onMessageDisplayed?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messagesShown]);

	return (
		<Flex my={2} alignItems="top" flexDirection={props.isMagic ? 'row' : 'row-reverse'}>
			<ChatAvatarBox mx={2} mt="6px" flexGrow={0} flexShrink={0}>
				<Avatar user={props.isMagic ? magicIdentity : viewer} size={24} />
			</ChatAvatarBox>
			<Box alignItems={props.isMagic ? 'flex-start' : 'flex-end'} maxWidth="80%">
				{bubbles}
				{isTyping && <TypingIndicator mt={messagesShown === 0 ? 0 : 1} key={messagesShown} />}
			</Box>
		</Flex>
	);
}

export const MessageInputBox = styled(Box)`
	border: none;
	padding: 10px;
	font-size: 16px;
	line-height: 24px;
	font-family: inherit;
	width: 100%;
	-webkit-appearance: none;
	appearance: none;
	background: white;
	border-radius: 6px;
	box-shadow:
		0px 0px 0px 1px ${(props) => props.theme.colors.lightBlue},
		0px 1px 2px ${(props) => rgba(props.theme.colors.lightBlue, 0.5)} inset;

	&:focus-within {
		outline: none;
		background: linear-gradient(${(props) => props.theme.colors.lightBlueLight}, white);
		box-shadow:
			0px 0px 0px 1px ${(props) => props.theme.colors.primary},
			0px 0px 0px 3px ${(props) => rgba(props.theme.colors.primary, 0.25)},
			0px 1px 2px ${(props) => props.theme.colors.lightBlueDark} inset;
	}
`;

type MagicConversation = { messages: string[]; isMagic: boolean }[];

function groupMessages(messages: graphql.SuggestionsChatMessage[]): MagicConversation {
	return messages.reduce((groupedMessages: MagicConversation, message) => {
		const isMagic = message.sender === graphql.SuggestionsChatMessageSender.Magic;
		const newGroup =
			groupedMessages.length === 0 ||
			groupedMessages[groupedMessages.length - 1].isMagic !== isMagic;

		if (newGroup) {
			return groupedMessages.concat([
				{
					messages: [message.text],
					isMagic,
				},
			]);
		} else {
			return [
				...groupedMessages.slice(0, -1),
				{
					messages: [...groupedMessages[groupedMessages.length - 1].messages, message.text],
					isMagic,
				},
			];
		}
	}, []);
}

const SuggestionWrapper = styled(Box)`
	margin-left: calc(24px + 2 * 8px);
	animation: 1s cubic-bezier(0, 1, 0, 1) ${slideIn};
	animation-fill-mode: backwards;
`;

const PreConversationAccessoryWrapper = styled(Box)`
	display: flex;
	justify-content: center;

	animation: 1s cubic-bezier(0, 1, 0, 1) ${slideIn};
	animation-delay: 250ms;
	animation-fill-mode: backwards;
`;

export function MagicChatModal<Suggestion>(props: {
	initialInput?: string;
	onDismiss: () => void;
	startChat: (reset: boolean) => Promise<{ chat: graphql.SuggestionsChat }>;
	starting: boolean;
	advanceChat: (
		chatID: string,
		userResponse: string,
	) => Promise<{ chat: graphql.SuggestionsChat; suggestion: Suggestion | null }>;
	advancing: boolean;
	renderSuggestion: (suggestion: Suggestion) => React.ReactNode;
	translationKey: string;
	isPreConversation?: boolean;
	preConversationMessages?: string[];
	preConversationAccessory?: ReactNode;
	preConversationResult?: string | null;
	onReset?: () => void;
}) {
	const { t } = useTranslation();
	const [input, setInput] = useState(props.initialInput ?? '');
	const viewer = useAuthenticatedUser();

	const [reset, setReset] = useState(false);
	const [chatID, setChatID] = useState<string | null>(null);
	const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
	const [showSuggestion, setShowSuggestion] = useState(false);

	const preConversation = props.preConversationMessages
		? [
				{
					messages: props.preConversationMessages,
					isMagic: true,
				},
				...(props.preConversationResult
					? [{ messages: [props.preConversationResult], isMagic: false }]
					: []),
		  ]
		: [];

	const [conversation, setConversation] = useState<MagicConversation>(preConversation);

	const [inputAllowed, setInputAllowed] = useState(false);
	const inputEnabled = inputAllowed && input.trim().length > 0 && !props.isPreConversation;

	const endRef = useRef<null | HTMLDivElement>(null);

	const submitInput = async () => {
		if (conversation.length > 0 && conversation[conversation.length - 1].isMagic === false) {
			const extendedLastGroup = {
				messages: [...conversation[conversation.length - 1].messages, input],
				isMagic: false,
			};

			setConversation(conversation.slice(0, -1).concat([extendedLastGroup]));
		} else {
			setConversation([
				...conversation,
				{
					messages: [input],
					isMagic: false,
				},
			]);
		}
		setInput('');
		setInputAllowed(false);

		const { chat, suggestion } = await props.advanceChat(chatID!, input);
		setConversation(preConversation.concat(groupMessages(chat.messages)));

		if (suggestion) {
			setSuggestion(suggestion);
		}
	};

	const noConversation = conversation.length === 0;

	useEffect(() => {
		(async () => {
			if (!chatID && !props.isPreConversation && !props.starting) {
				setInputAllowed(false);

				const { chat } = await props.startChat(reset);

				setChatID(chat.id);
				setConversation(preConversation.concat(groupMessages(chat.messages)));
			}

			if (noConversation && props.isPreConversation) {
				setInputAllowed(false);
				setConversation(preConversation);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [noConversation, chatID, props.isPreConversation]);

	// Scroll to bottom when messages change
	const scrollIntoView = () => {
		endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	useEffect(() => {
		if (suggestion) {
			scrollIntoView();
		}
	}, [suggestion, showSuggestion]);

	const resetConversation = () => {
		setReset(true);
		setSuggestion(null);
		setShowSuggestion(false);
		setConversation([]);
		setChatID(null);
		props.onReset?.();
	};

	const loadingConversation = conversation.concat(
		props.advancing ? [{ messages: [], isMagic: true }] : [],
	);

	return (
		<Flex width={600} flexDirection="column" flexShrink={1} flexGrow={1}>
			<Flex p={3} alignItems="center">
				{/* TODO: Use generic title and plug in object name */}
				<Heading flexGrow={1}>{t(`${props.translationKey}.magic.modal.title`)}</Heading>
				<Button
					mr={3}
					type="secondary"
					small
					title={t('magic.reset')}
					flexGrow={0}
					flexShrink={0}
					onClick={() => resetConversation()}
				/>
				<IconButton type="plain" icon={<CloseIcon />} onClick={() => props.onDismiss()} />
			</Flex>
			<Container
				flexGrow={1}
				flexShrink={1}
				style={{ height: '600px' }}
				justifyContent="space-between"
			>
				<Box p={3} style={{ overflowY: 'scroll' }} flexGrow={1}>
					{loadingConversation.map((group, index) => (
						<BubbleGroup
							key={index}
							messages={group.messages.map((message) =>
								group.isMagic ? message.replaceAll('{username}', viewer.firstName) : message,
							)}
							isMagic={group.isMagic}
							onMessageDisplayed={() => scrollIntoView()}
							onStoppedTyping={() => setInputAllowed(true)}
						/>
					))}
					{suggestion && (
						<>
							<BubbleGroup
								messages={[t(`${props.translationKey}.magic.suggestion`)]}
								isMagic={true}
								onStoppedTyping={() => setShowSuggestion(true)}
							/>
							{showSuggestion && (
								<SuggestionWrapper>{props.renderSuggestion(suggestion)}</SuggestionWrapper>
							)}
						</>
					)}
					{props.isPreConversation && inputAllowed && (
						<PreConversationAccessoryWrapper>
							{props.preConversationAccessory}
						</PreConversationAccessoryWrapper>
					)}
					<div ref={endRef} />
				</Box>
				<Separator />
				<Flex p={3} alignItems="flex-end" justifyContent="flex-start">
					<MessageInputBox mr={2} flexGrow={1}>
						<PlainInput
							onChange={setInput}
							onKeyDown={(event) => {
								if (event.key === 'Enter' && inputEnabled) {
									submitInput();
									event.preventDefault();
								}
							}}
							autoFocus
							value={input}
						/>
					</MessageInputBox>
					<Button
						title={t('goals.magic.input.submit')}
						icon={<SendIcon />}
						type="transparent"
						disabled={!inputEnabled}
						onClick={() => {
							// Don't return promise, otherwise the button will show loading state
							submitInput();
							return;
						}}
						flexShrink={0}
					/>
				</Flex>
			</Container>
		</Flex>
	);
}
